/** Keep the HTML fallback useful while Angular's authenticated bootstrap is pending. */
export function prepareStartupDocument(documentRef: Document = document): {
  stylesReady: Promise<void>;
  complete(): void;
  fail(): void;
} {
  documentRef.querySelectorAll<HTMLLinkElement>('[data-pulse-startup-font]')
    .forEach((link) => { link.media = 'all'; });
  const fallback = documentRef.querySelector<HTMLElement>('[data-pulse-bootstrap]');
  // An empty href resolves against <base>, which can discard a deep link.
  const bindRetry = (element: HTMLElement | null | undefined): void => {
    const retry = element?.querySelector<HTMLAnchorElement>('a');
    if (retry && documentRef.location) {
      retry.href = documentRef.location.href;
      // Navigating to the same fragment does not reload the document.
      retry.onclick = (event) => {
        event.preventDefault();
        documentRef.location.reload();
      };
    }
  };
  bindRetry(fallback);
  const copy = fallback?.cloneNode(true) as HTMLElement | undefined;
  // Angular clears app-root. Keep the very same animated node outside it until
  // the first StartupLoaderComponent releases its existing readiness gate.
  if (fallback) {
    fallback.setAttribute('data-pulse-startup-managed', '');
    if (fallback.parentElement !== documentRef.body) documentRef.body.append(fallback);
  }
  const stylesReady = Promise.all(
    Array.from(documentRef.querySelectorAll<HTMLLinkElement>(
      'link[rel="stylesheet"]:not([data-pulse-startup-font])',
    )).map((link) => {
      let alreadyLoaded = link.sheet !== null;
      if (alreadyLoaded && new URL(link.href, documentRef.baseURI).origin === documentRef.location?.origin) {
        try {
          // Chromium can expose an opaque sheet even for a failed same-origin request.
          alreadyLoaded = !!link.sheet?.cssRules;
        } catch {
          alreadyLoaded = false;
        }
      }
      if (alreadyLoaded) {
        link.media = 'all';
        return Promise.resolve();
      }
      return new Promise<void>((resolve, reject) => {
        const cleanup = (): void => {
          clearTimeout(deadline);
          link.removeEventListener('load', loaded);
          link.removeEventListener('error', failed);
        };
        const loaded = (): void => {
          cleanup();
          link.media = 'all';
          resolve();
        };
        const failed = (): void => {
          cleanup();
          reject(new Error('Application stylesheet could not load'));
        };
        link.addEventListener('load', loaded);
        link.addEventListener('error', failed);
        // The request may have failed before main attached these listeners.
        // A deadline also bounds a stalled request; it never releases the gate.
        const deadline = setTimeout(failed, 30000);
      });
    }),
  ).then(() => undefined);
  const updateStatus = (element: HTMLElement | null | undefined, message: string): void => {
    const status = element?.querySelector('[data-pulse-bootstrap-status]');
    if (status) status.textContent = message;
    // Error-only selectors need not be present in inlined critical CSS.
    const retryLink = element?.querySelector<HTMLAnchorElement>('a');
    if (retryLink) {
      retryLink.style.animation = 'none';
      retryLink.style.visibility = 'visible';
    }
  };
  const timer = setTimeout(() => {
    fallback?.setAttribute('data-pulse-startup-slow', '');
    updateStatus(documentRef.querySelector<HTMLElement>('[data-pulse-bootstrap]'),
      'Der Start dauert länger als erwartet. Bitte Verbindung prüfen oder erneut laden.');
  }, 15000);
  const release = (): void => clearTimeout(timer);
  fallback?.addEventListener('pulse-startup-released', release, { once: true });
  return {
    stylesReady,
    complete: () => {
      // Keep the slow-start timer while the owning component waits for APIs.
      if (!fallback?.hasAttribute('data-pulse-startup-claimed')) {
        release();
        fallback?.removeEventListener('pulse-startup-released', release);
        fallback?.remove();
      }
    },
    fail: () => {
      release();
      fallback?.removeEventListener('pulse-startup-released', release);
      let current = documentRef.querySelector<HTMLElement>('[data-pulse-bootstrap]');
      if (!current && copy) {
        bindRetry(copy);
        documentRef.body.append(copy);
        current = copy;
      }
      current?.setAttribute('aria-busy', 'false');
      updateStatus(current, 'Die Anwendung konnte nicht starten. Bitte erneut laden.');
    },
  };
}
